import { memo } from "react";
import { Gift, Leaf } from "lucide-react";
import type { GoldenSerenityTemplateProps } from "./types";
import { GS, formatDate, isEventEveOrDay, isRsvpCutoffPassed, shouldShowGiftRegistry } from "./utils";
import { AmbientGlow, BotanicalCorner, OrganicDivider } from "./decorations";
import { getCountryDisplayName } from "@/utils/eventUtils";

export const GoldenSerenityHero = memo(({ event, config, locale, t, onRsvpClick }: GoldenSerenityTemplateProps) => {
  const localeCode = (locale || "ro") as "ro" | "en";
  const heroTagline = event.heroText?.[localeCode] || event.heroText?.ro || event.heroText?.en;

  const includesWedding = event.includesWedding ?? true;
  const includesBaptism = event.includesBaptism ?? false;
  const isBaptismOnly = includesBaptism && !includesWedding;
  const isWeddingAndBaptism = includesBaptism && includesWedding;
  // JUB-112 fallout: golden_serenity never rendered the date-label copy that
  // botez_bokeh (and the retired classic_romance) show above the date. Once the
  // remap made golden_serenity the default theme, every migrated event silently
  // lost "Îl botezăm pe" / "Ne căsătorim pe". Same keys as botez-bokeh/Hero.tsx.
  const dateLabelKey = isBaptismOnly
    ? "events.form.dateLabelBaptism"
    : isWeddingAndBaptism
      ? "events.form.dateLabelWeddingAndBaptism"
      : "events.form.dateLabelWedding";

  const heroTitle =
    isBaptismOnly && event.starBaptism
      ? t("events.form.heroTitleBaptismOnly", { name: event.starBaptism })
      : isWeddingAndBaptism
        ? t("events.form.heroTitleWeddingAndBaptism")
        : event.name;
  const nameParts =
    event.starBride && event.starGroom ? [event.starBride, event.starGroom] : null;

  const countryDisplayName = getCountryDisplayName(event.country, locale);
  const firstCeremonyCity =
    event.ceremonies?.civil?.enabled && event.ceremonies.civil.city
      ? event.ceremonies.civil.city
      : event.ceremonies?.religious?.enabled && event.ceremonies.religious.city
        ? event.ceremonies.religious.city
        : event.ceremonies?.party?.enabled && event.ceremonies.party.city
          ? event.ceremonies.party.city
          : null;
  const locationLabel = [firstCeremonyCity, countryDisplayName].filter(Boolean).join(", ");

  const promoteGiftCta =
    shouldShowGiftRegistry(event) && (isRsvpCutoffPassed(event) || isEventEveOrDay(event));
  const rsvpVisible =
    Boolean(event.rsvpSettings?.rsvpEnabled) && !isRsvpCutoffPassed(event) && !promoteGiftCta;
  const postCutoffRegistryVisible =
    Boolean(event.rsvpSettings?.rsvpEnabled) && promoteGiftCta;

  const ctaClassName =
    "inline-flex items-center gap-2 px-8 sm:px-12 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-500 hover:shadow-[0_8px_24px_rgba(201,168,106,0.25)]";
  const ctaStyle = {
    background: GS.gold,
    // JUB-90: creamLight-on-gold was 2.1:1 (fails WCAG AA); GS.ink gives ~5.7:1
    color: GS.ink,
    fontFamily: config.typography.body,
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center px-4 pt-24 sm:pt-20 overflow-hidden"
      style={{
        background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.cream}, ${GS.creamDeep})`,
      }}
    >
      <AmbientGlow />
      <BotanicalCorner position="top-left" />
      <BotanicalCorner position="top-right" />
      <BotanicalCorner position="bottom-left" />
      <BotanicalCorner position="bottom-right" />

      <div className="max-w-5xl mx-auto py-16 sm:py-20 w-full relative z-10">
        <div className="text-center space-y-10 sm:space-y-14">
          {/* Nature-inspired overline (event tagline) */}
          <div className="flex items-center justify-center gap-3 px-6">
            <Leaf className="w-4 h-4 shrink-0 opacity-60" style={{ color: GS.sageDeep }} aria-hidden="true" />
            <p
              className="text-xs tracking-[0.2em] uppercase font-light break-words"
              style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
            >
              {heroTagline || t("events.form.template.foreverAlways")}
            </p>
            <Leaf
              className="w-4 h-4 shrink-0 opacity-60 rotate-180"
              style={{ color: GS.sageDeep }}
              aria-hidden="true"
            />
          </div>

          {/* Names with organic flow */}
          <div className="space-y-6 sm:space-y-8">
            {nameParts && !isBaptismOnly && !isWeddingAndBaptism ? (
              // JUB-90: a single <h1> per page — the second name is visually
              // identical but is not itself a heading (avoids two level-1
              // headings for "Elena" / "Andrei" on the same page outline).
              <>
                <h1
                  className="text-5xl sm:text-7xl lg:text-8xl leading-[1.1] font-medium break-words"
                  style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                >
                  {nameParts[0]}
                </h1>
                <OrganicDivider width={80} />
                <p
                  className="text-5xl sm:text-7xl lg:text-8xl leading-[1.1] font-medium break-words"
                  style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                >
                  {nameParts[1]}
                </p>
              </>
            ) : (
              <h1
                className="text-4xl sm:text-6xl lg:text-7xl leading-[1.1] font-medium px-2 break-words"
                style={{ color: config.palette.text, fontFamily: config.typography.heading }}
              >
                {heroTitle}
              </h1>
            )}
            {isWeddingAndBaptism && (event.starBride || event.starGroom) && (
              <p
                className="text-xl sm:text-2xl opacity-90 break-words"
                style={{ color: config.palette.text, fontFamily: config.typography.heading }}
              >
                {event.starBride && event.starGroom
                  ? `${event.starBride} & ${event.starGroom}`
                  : event.starBride || event.starGroom}
              </p>
            )}
            {isWeddingAndBaptism && (event.starBaptism || event.starBaptism2) && (
              <p
                className="text-lg sm:text-xl opacity-80 break-words"
                style={{ color: config.palette.text, fontFamily: config.typography.heading }}
              >
                {[event.starBaptism, event.starBaptism2].filter(Boolean).join(" & ")}
              </p>
            )}
          </div>

          {/* Date & Location */}
          {(event.date || locationLabel) && (
            <div className="space-y-4 pt-4">
              {event.date && (
                <p
                  className="text-3xl sm:text-4xl font-medium italic text-balance"
                  style={{ color: config.palette.mutedText, fontFamily: config.typography.heading }}
                >
                  {`${t(dateLabelKey)} ${formatDate(event.date, locale, { day: "numeric", month: "long", year: "numeric" })}`}
                </p>
              )}
              {locationLabel && (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-1 h-1 rounded-full opacity-40" style={{ backgroundColor: GS.gold }} aria-hidden="true" />
                  <p
                    className="text-sm tracking-wide"
                    style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                  >
                    {locationLabel}
                  </p>
                  <div className="w-1 h-1 rounded-full opacity-40" style={{ backgroundColor: GS.gold }} aria-hidden="true" />
                </div>
              )}
            </div>
          )}

          {/* Warm CTA */}
          {rsvpVisible && (
            <div className="pt-2">
              <button type="button" onClick={onRsvpClick} className={ctaClassName} style={ctaStyle}>
                {t("events.form.template.rsvpNow")}
              </button>
            </div>
          )}

          {/* JUB-23: registry contribution link shown after the RSVP cutoff */}
          {postCutoffRegistryVisible && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => {
                  const element = document.getElementById("gifts");
                  if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={ctaClassName}
                style={ctaStyle}
              >
                <Gift className="h-4 w-4" aria-hidden="true" />
                {t("events.form.template.contributeGift")}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
});

GoldenSerenityHero.displayName = "GoldenSerenityHero";
